import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useHqAccess } from './settings/hqAccess';
import {
  getLibraryResources,
  createLibraryResource,
  uploadLibraryFile,
  updateLibraryResource,
  deleteLibraryResource,
  getLibraryDownloadUrl,
  LibraryResource,
  CreateLibraryResourceRequest,
} from '../api/client';
import { openOneDrivePicker } from '../utils/onedrive-picker';

// Department order for sidebar
const DEPARTMENT_ORDER = [
  'Sales & Design',
  'CAD & Development',
  'Project Management',
  'Construction / Field',
  'Accounting / Finance',
  'Company',
];

export function LibraryPage() {
  useAuth();
  // BUILD-039: library management follows the server-resolved permission (the server checks it again on every change)
  const isAdmin = useHqAccess().can('hq.library.manage');
  const [hierarchy, setHierarchy] = useState<Record<string, Record<string, LibraryResource[]>>>({});
  const [allItems, setAllItems] = useState<LibraryResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Navigation
  const [activeDept, setActiveDept] = useState<string | null>(null);
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const [searchQuery, setSearchQuery] = useState('');

  // Admin state
  const [showAdmin, setShowAdmin] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateLibraryResourceRequest & { url?: string }>({ department: '', category: '', title: '', url: '', description: '', ordinal: 0 });
  const [newDept, setNewDept] = useState('');
  const [newCat, setNewCat] = useState('');
  const [useNewDept, setUseNewDept] = useState(false);
  const [useNewCat, setUseNewCat] = useState(false);
  const [resourceType, setResourceType] = useState<'link' | 'file' | 'bulk' | 'note'>('link');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [bulkFiles, setBulkFiles] = useState<File[]>([]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  }, []);

  const loadResources = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getLibraryResources(showAdmin);
      setHierarchy(data.hierarchy || {});
      setAllItems(data.items || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load library');
    } finally {
      setLoading(false);
    }
  }, [showAdmin]);

  useEffect(() => { loadResources(); }, [loadResources]);

  // Auto-expand first department and select first category
  useEffect(() => {
    if (!activeDept && Object.keys(hierarchy).length > 0) {
      const firstDept = DEPARTMENT_ORDER.find(d => hierarchy[d]) || Object.keys(hierarchy)[0];
      setActiveDept(firstDept);
      setExpandedDepts(new Set([firstDept]));
      const cats = Object.keys(hierarchy[firstDept] || {});
      if (cats.length > 0) setActiveCat(cats[0]);
    }
  }, [hierarchy, activeDept]);

  const existingDepts = [...new Set(allItems.map(i => i.department))].sort();
  const existingCats = [...new Set(allItems.map(i => i.category))].sort();

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleAdd = async () => {
    const department = useNewDept ? newDept.trim() : form.department;
    const category = useNewCat ? newCat.trim() : form.category;
    if (!department) { showToast('Department is required', 'error'); return; }
    if (!category) { showToast('Category is required', 'error'); return; }
    if (!form.title.trim()) { showToast('Title is required', 'error'); return; }

    setSubmitting(true);
    try {
      if (resourceType === 'file' && uploadFile) {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(uploadFile);
        });
        await uploadLibraryFile({ department, category, title: form.title.trim(), fileName: uploadFile.name, fileContent: base64, contentType: uploadFile.type || 'application/octet-stream', description: form.description, ordinal: form.ordinal });
      } else {
        await createLibraryResource({ ...form, department, category, url: resourceType === 'link' ? form.url : undefined });
      }
      showToast('Resource created', 'success');
      setAdding(false);
      resetForm();
      await loadResources();
    } catch (e: any) { showToast(e.message || 'Failed to create', 'error'); }
    finally { setSubmitting(false); }
  };

  const handleBulkUpload = async () => {
    const department = useNewDept ? newDept.trim() : form.department;
    const category = useNewCat ? newCat.trim() : form.category;
    if (!department) { showToast('Department is required', 'error'); return; }
    if (!category) { showToast('Category is required', 'error'); return; }
    if (bulkFiles.length === 0) { showToast('Select files', 'error'); return; }

    setBulkUploading(true);
    setBulkProgress({ done: 0, total: bulkFiles.length });
    let success = 0;
    for (let i = 0; i < bulkFiles.length; i++) {
      const file = bulkFiles[i];
      try {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
        await uploadLibraryFile({ department, category, title: file.name.replace(/\.[^/.]+$/, ''), fileName: file.name, fileContent: base64, contentType: file.type || 'application/octet-stream', ordinal: (i + 1) * 10 });
        success++;
      } catch { /* skip */ }
      setBulkProgress({ done: i + 1, total: bulkFiles.length });
    }
    setBulkUploading(false);
    setBulkProgress(null);
    showToast(`${success}/${bulkFiles.length} files uploaded`, success === bulkFiles.length ? 'success' : 'error');
    setAdding(false);
    resetForm();
    await loadResources();
  };

  const handleUpdate = async () => {
    if (!editingId) return;
    const department = useNewDept ? newDept.trim() : form.department;
    const category = useNewCat ? newCat.trim() : form.category;
    setSubmitting(true);
    try {
      await updateLibraryResource(editingId, { ...form, department, category, url: form.url || undefined });
      showToast('Updated', 'success');
      setEditingId(null);
      resetForm();
      await loadResources();
    } catch (e: any) { showToast(e.message || 'Failed', 'error'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete permanently?')) return;
    try { await deleteLibraryResource(id); showToast('Deleted', 'success'); await loadResources(); }
    catch (e: any) { showToast(e.message || 'Failed', 'error'); }
  };

  const handleDownload = async (item: LibraryResource) => {
    try { const { downloadUrl } = await getLibraryDownloadUrl(item.id); window.open(downloadUrl, '_blank'); }
    catch (e: any) { showToast(e.message || 'Download failed', 'error'); }
  };

  const startEdit = (item: LibraryResource) => {
    setEditingId(item.id);
    setForm({ department: item.department, category: item.category, title: item.title, url: item.url || '', description: item.description || '', ordinal: item.ordinal });
    setUseNewDept(false); setUseNewCat(false); setAdding(false);
  };

  const resetForm = () => {
    setForm({ department: activeDept || '', category: activeCat || '', title: '', url: '', description: '', ordinal: 0 });
    setNewDept(''); setNewCat(''); setUseNewDept(false); setUseNewCat(false);
    setResourceType('link'); setUploadFile(null); setBulkFiles([]); setBulkProgress(null);
  };

  const cancelForm = () => { setAdding(false); setEditingId(null); resetForm(); };

  const handleDrop = (e: React.DragEvent) => { e.preventDefault(); const files: File[] = []; if (e.dataTransfer.items) { for (let i = 0; i < e.dataTransfer.items.length; i++) { const f = e.dataTransfer.items[i].getAsFile(); if (f) files.push(f); } } else { for (let i = 0; i < e.dataTransfer.files.length; i++) files.push(e.dataTransfer.files[i]); } setBulkFiles(prev => [...prev, ...files]); };

  // ─── Display Logic ───────────────────────────────────────────────────────

  function getItemIcon(item: LibraryResource) {
    if (item.fileKey) return '📄';
    if (!item.url) return '📝';
    if (item.url.includes('loom.com')) return '🎬';
    if (item.url.includes('youtube') || item.url.includes('youtu.be')) return '▶️';
    if (item.url.includes('screencast')) return '🖥️';
    return '🔗';
  }

  function getItemLabel(item: LibraryResource) {
    if (item.fileKey) return item.fileName || 'File';
    if (!item.url) return 'Note';
    if (item.url.includes('loom.com')) return 'Loom';
    if (item.url.includes('youtube') || item.url.includes('youtu.be')) return 'YouTube';
    if (item.url.includes('screencast')) return 'Screencast';
    return 'Link';
  }

  // Search
  const isSearching = searchQuery.trim().length > 0;
  const searchResults = isSearching ? allItems.filter(i =>
    i.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (i.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.department.toLowerCase().includes(searchQuery.toLowerCase()) ||
    i.category.toLowerCase().includes(searchQuery.toLowerCase())
  ) : [];

  // Current items based on selection
  const displayedItems = isSearching ? searchResults : (activeDept && activeCat && hierarchy[activeDept]?.[activeCat]) || [];

  // Sidebar departments (ordered)
  const sidebarDepts = DEPARTMENT_ORDER.filter(d => hierarchy[d]);
  // Also include any departments not in the predefined order
  const extraDepts = Object.keys(hierarchy).filter(d => !DEPARTMENT_ORDER.includes(d));
  const allDepts = [...sidebarDepts, ...extraDepts];

  const toggleDept = (dept: string) => {
    const next = new Set(expandedDepts);
    if (next.has(dept)) next.delete(dept); else next.add(dept);
    setExpandedDepts(next);
  };

  const selectCategory = (dept: string, cat: string) => {
    setActiveDept(dept);
    setActiveCat(cat);
    setSearchQuery('');
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="h-full flex flex-col">
      {toast && <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>{toast.message}</div>}

      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Library</h1>
          <p className="text-xs text-gray-500">Training, SOPs, documents, and company resources</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search..." className="w-48 px-3 py-1.5 pl-8 border border-gray-300 rounded-lg text-sm focus:ring-[#4AABDE] focus:border-[#4AABDE] outline-none" />
            <svg className="absolute left-2.5 top-2 w-3.5 h-3.5 text-gray-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
            {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-2 top-2 text-gray-400 hover:text-gray-600 text-xs">✕</button>}
          </div>
          {/* View Toggle */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button onClick={() => setViewMode('card')} className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'card' ? 'bg-[#4AABDE] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Cards</button>
            <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 text-xs font-medium ${viewMode === 'list' ? 'bg-[#4AABDE] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>List</button>
          </div>
          {isAdmin && <button onClick={() => setShowAdmin(!showAdmin)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${showAdmin ? 'bg-[#4AABDE] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>{showAdmin ? 'Exit Admin' : 'Manage'}</button>}
        </div>
      </div>

      {/* Admin Panel (collapsible) */}
      {showAdmin && isAdmin && (
        <div className="bg-gray-50 border-b border-gray-200 px-6 py-3 flex-shrink-0">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-600">Admin</span>
            <button onClick={() => { setAdding(true); setEditingId(null); resetForm(); }} className="px-3 py-1 rounded bg-[#1a2332] text-white text-xs font-medium hover:bg-[#2a3342]">+ Add Resource</button>
          </div>
          {(adding || editingId) && (
            <div className="bg-white border border-blue-200 rounded-lg p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                {/* Department */}
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Department</label>
                  {!useNewDept ? (
                    <div className="flex gap-1">
                      <select value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} className="flex-1 px-2 py-1.5 border rounded text-sm">
                        <option value="">Select...</option>
                        {existingDepts.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <button type="button" onClick={() => setUseNewDept(true)} className="text-[10px] text-[#4AABDE] hover:underline whitespace-nowrap">+New</button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <input type="text" value={newDept} onChange={e => setNewDept(e.target.value)} placeholder="New department..." className="flex-1 px-2 py-1.5 border rounded text-sm" />
                      <button type="button" onClick={() => setUseNewDept(false)} className="text-[10px] text-gray-500 hover:underline">Existing</button>
                    </div>
                  )}
                </div>
                {/* Category */}
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Category</label>
                  {!useNewCat ? (
                    <div className="flex gap-1">
                      <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="flex-1 px-2 py-1.5 border rounded text-sm">
                        <option value="">Select...</option>
                        {existingCats.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <button type="button" onClick={() => setUseNewCat(true)} className="text-[10px] text-[#4AABDE] hover:underline whitespace-nowrap">+New</button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <input type="text" value={newCat} onChange={e => setNewCat(e.target.value)} placeholder="New category..." className="flex-1 px-2 py-1.5 border rounded text-sm" />
                      <button type="button" onClick={() => setUseNewCat(false)} className="text-[10px] text-gray-500 hover:underline">Existing</button>
                    </div>
                  )}
                </div>
                {/* Title */}
                {resourceType !== 'bulk' && (
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Title</label>
                    <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Title..." className="w-full px-2 py-1.5 border rounded text-sm" />
                  </div>
                )}
                {/* Resource Type */}
                {adding && (
                  <div>
                    <label className="text-xs font-medium text-gray-600 block mb-1">Type</label>
                    <div className="flex flex-wrap gap-3 mt-0.5">
                      <label className="inline-flex items-center gap-1 cursor-pointer text-xs"><input type="radio" checked={resourceType === 'link'} onChange={() => { setResourceType('link'); setUploadFile(null); setBulkFiles([]); }} className="text-[#4AABDE]" /><span>Link</span></label>
                      <label className="inline-flex items-center gap-1 cursor-pointer text-xs"><input type="radio" checked={resourceType === 'file'} onChange={() => { setResourceType('file'); setBulkFiles([]); }} className="text-[#4AABDE]" /><span>File</span></label>
                      <label className="inline-flex items-center gap-1 cursor-pointer text-xs"><input type="radio" checked={resourceType === 'bulk'} onChange={() => { setResourceType('bulk'); setUploadFile(null); }} className="text-[#4AABDE]" /><span>Bulk</span></label>
                      <label className="inline-flex items-center gap-1 cursor-pointer text-xs"><input type="radio" checked={resourceType === 'note'} onChange={() => { setResourceType('note'); setUploadFile(null); setBulkFiles([]); }} className="text-[#4AABDE]" /><span>Note</span></label>
                    </div>
                  </div>
                )}
                {/* URL */}
                {resourceType === 'link' && (
                  <div className="sm:col-span-2">
                    <label className="text-xs font-medium text-gray-600 block mb-1">URL</label>
                    <div className="flex gap-2">
                      <input type="url" value={form.url} onChange={e => setForm({ ...form, url: e.target.value })} placeholder="https://..." className="flex-1 px-2 py-1.5 border rounded text-sm" />
                      <button type="button" onClick={async () => { try { const r = await openOneDrivePicker(); if (r) setForm(f => ({ ...f, url: r.url, title: f.title || r.name })); } catch (e: any) { showToast(e.message, 'error'); } }} className="px-2 py-1.5 bg-white border rounded text-xs text-gray-700 hover:bg-gray-50 whitespace-nowrap">OneDrive</button>
                    </div>
                  </div>
                )}
                {/* File upload */}
                {resourceType === 'file' && adding && (
                  <div><label className="text-xs font-medium text-gray-600 block mb-1">File</label><input type="file" onChange={e => setUploadFile(e.target.files?.[0] || null)} className="text-xs" /></div>
                )}
                {/* Bulk */}
                {resourceType === 'bulk' && adding && (
                  <div className="sm:col-span-2">
                    <div onDrop={handleDrop} onDragOver={e => e.preventDefault()} className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-[#4AABDE] transition-colors">
                      <p className="text-sm text-gray-600">Drag files here</p>
                    </div>
                    <div className="flex gap-2 mt-2">
                      <button type="button" onClick={() => { const i = document.createElement('input'); i.type = 'file'; i.multiple = true; i.accept = '*/*'; i.onchange = (ev: any) => setBulkFiles(p => [...p, ...Array.from(ev.target.files || []) as File[]]); i.click(); }} className="px-2 py-1 bg-white border rounded text-xs hover:bg-gray-50">Select files</button>
                      <button type="button" onClick={() => bulkInputRef.current?.click()} className="px-2 py-1 bg-white border rounded text-xs hover:bg-gray-50">Select folder</button>
                    </div>
                    <input ref={bulkInputRef} type="file" multiple
                      // @ts-ignore — webkitdirectory not in React types
                      webkitdirectory=""
                      onChange={e => { setBulkFiles(p => [...p, ...Array.from(e.target.files || [])]); e.target.value = ''; }} className="hidden" />
                    {bulkFiles.length > 0 && <div className="mt-2 text-xs text-gray-600">{bulkFiles.length} files ready</div>}
                    {bulkProgress && <div className="mt-1 w-full bg-gray-200 rounded-full h-1.5"><div className="bg-[#4AABDE] h-1.5 rounded-full" style={{ width: `${(bulkProgress.done / bulkProgress.total) * 100}%` }} /></div>}
                  </div>
                )}
                {/* Description */}
                {resourceType !== 'bulk' && (
                  <div className="sm:col-span-2"><label className="text-xs font-medium text-gray-600 block mb-1">Description</label><input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional..." className="w-full px-2 py-1.5 border rounded text-sm" /></div>
                )}
              </div>
              <div className="flex gap-2 mt-3">
                {resourceType === 'bulk' && adding ? (
                  <button onClick={handleBulkUpload} disabled={bulkUploading || bulkFiles.length === 0} className="px-3 py-1.5 rounded bg-[#4AABDE] text-white text-xs font-medium disabled:opacity-50">{bulkUploading ? 'Uploading...' : `Upload ${bulkFiles.length} files`}</button>
                ) : (
                  <button onClick={adding ? handleAdd : handleUpdate} disabled={submitting} className="px-3 py-1.5 rounded bg-[#4AABDE] text-white text-xs font-medium disabled:opacity-50">{submitting ? 'Saving...' : (adding ? 'Create' : 'Save')}</button>
                )}
                <button onClick={cancelForm} className="px-3 py-1.5 rounded bg-gray-200 text-gray-700 text-xs">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main: Sidebar + Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar — Department → Category tree */}
        <div className="w-60 bg-gray-50 border-r border-gray-200 overflow-y-auto flex-shrink-0">
          <div className="p-3">
            {loading ? (
              <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="h-6 bg-gray-200 rounded animate-pulse" />)}</div>
            ) : allDepts.length === 0 ? (
              <p className="text-xs text-gray-400">No content yet</p>
            ) : (
              <ul className="space-y-0.5">
                {allDepts.map(dept => {
                  const categories = Object.keys(hierarchy[dept] || {}).sort();
                  const isExpanded = expandedDepts.has(dept);
                  const itemCount = Object.values(hierarchy[dept] || {}).reduce((sum, arr) => sum + arr.length, 0);
                  return (
                    <li key={dept}>
                      <button onClick={() => toggleDept(dept)} className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded text-left text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors">
                        <svg className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" /></svg>
                        <span className="truncate flex-1">{dept}</span>
                        <span className="text-[10px] text-gray-400">{itemCount}</span>
                      </button>
                      {isExpanded && categories.length > 0 && (
                        <ul className="ml-4 mt-0.5 space-y-0.5">
                          {categories.map(cat => {
                            const isActive = activeDept === dept && activeCat === cat;
                            const catCount = hierarchy[dept][cat]?.length || 0;
                            return (
                              <li key={cat}>
                                <button onClick={() => selectCategory(dept, cat)} className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${isActive ? 'bg-[#4AABDE]/10 text-[#4AABDE] font-medium' : 'text-gray-600 hover:bg-gray-100'}`}>
                                  <span className="flex items-center justify-between"><span className="truncate">{cat}</span>{catCount > 0 && <span className="text-[10px] text-gray-400 ml-1">{catCount}</span>}</span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Right — Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="text-center py-16 text-gray-400">Loading...</div>}
          {error && <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>}

          {!loading && !error && (
            <>
              {/* Header */}
              <div className="mb-4">
                {isSearching ? (
                  <h2 className="text-lg font-semibold text-gray-800">Search: "{searchQuery}" <span className="text-xs font-normal text-gray-400">({searchResults.length} results)</span></h2>
                ) : activeDept && activeCat ? (
                  <h2 className="text-lg font-semibold text-gray-800">{activeCat} <span className="text-xs font-normal text-gray-400 ml-1">{activeDept} • {displayedItems.length} items</span></h2>
                ) : (
                  <h2 className="text-lg font-semibold text-gray-400">Select a category</h2>
                )}
              </div>

              {/* Empty state */}
              {displayedItems.length === 0 && !isSearching && activeCat && (
                <div className="text-center py-12 text-gray-400 text-sm">
                  <p>No resources in this category yet.</p>
                  {isAdmin && <p className="mt-1">Click Manage → + Add Resource to add content here.</p>}
                </div>
              )}

              {/* Card View */}
              {viewMode === 'card' && displayedItems.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {displayedItems.map(item => {
                    const hasLink = !!item.url;
                    const hasFile = !!item.fileKey;
                    const isClickable = hasLink || hasFile;
                    const Wrapper = isClickable ? 'a' : 'div';
                    const wrapperProps: any = isClickable
                      ? hasLink ? { href: item.url, target: '_blank', rel: 'noopener noreferrer' }
                      : { href: '#', onClick: (e: any) => { e.preventDefault(); handleDownload(item); } }
                      : {};

                    return (
                      <div key={item.id} className="relative group">
                        <Wrapper {...wrapperProps} className={`flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-xl transition-all ${isClickable ? 'hover:border-[#4AABDE] hover:shadow-md cursor-pointer' : ''}`}>
                          <span className="text-xl shrink-0">{getItemIcon(item)}</span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-medium leading-tight ${isClickable ? 'group-hover:text-[#4AABDE]' : ''} text-gray-900`}>{item.title}</p>
                            {item.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.description}</p>}
                            <span className="text-[10px] text-gray-400 mt-1 block">{getItemLabel(item)}</span>
                          </div>
                        </Wrapper>
                        {showAdmin && <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 flex gap-1"><button onClick={() => startEdit(item)} className="p-1 bg-white border rounded shadow-sm text-[10px] text-[#4AABDE]">Edit</button><button onClick={() => handleDelete(item.id)} className="p-1 bg-white border rounded shadow-sm text-[10px] text-red-500">Del</button></div>}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* List View */}
              {viewMode === 'list' && displayedItems.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-gray-50 border-b text-left text-xs font-semibold text-gray-500 uppercase">
                      <th className="px-3 py-2">Title</th>
                      <th className="px-3 py-2 w-32">Type</th>
                      {isSearching && <th className="px-3 py-2 w-40">Location</th>}
                      <th className="px-3 py-2 w-24">Date</th>
                      {showAdmin && <th className="px-3 py-2 w-20 text-right">Actions</th>}
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100">
                      {displayedItems.map(item => {
                        const hasLink = !!item.url;
                        const hasFile = !!item.fileKey;
                        return (
                          <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-3 py-2">
                              {hasLink ? (
                                <a href={item.url!} target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:text-[#4AABDE] font-medium">{item.title}</a>
                              ) : hasFile ? (
                                <button onClick={() => handleDownload(item)} className="text-gray-900 hover:text-[#4AABDE] font-medium text-left">{item.title}</button>
                              ) : (
                                <span className="text-gray-900 font-medium">{item.title}</span>
                              )}
                              {item.description && <p className="text-xs text-gray-400 truncate max-w-md">{item.description}</p>}
                            </td>
                            <td className="px-3 py-2 text-xs text-gray-500">{getItemIcon(item)} {getItemLabel(item)}</td>
                            {isSearching && <td className="px-3 py-2 text-xs text-gray-400">{item.department} › {item.category}</td>}
                            <td className="px-3 py-2 text-xs text-gray-400">{item.createdAt?.slice(0, 10)}</td>
                            {showAdmin && <td className="px-3 py-2 text-right"><button onClick={() => startEdit(item)} className="text-[#4AABDE] hover:underline text-xs mr-2">Edit</button><button onClick={() => handleDelete(item.id)} className="text-red-500 hover:underline text-xs">Del</button></td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
